import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {ReactElement, ReactNode} from 'react'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import {addLocalePrefix} from '@/utilities/add-locale-prefix'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {Card} from '@/products/flexible-web-toolkit/components/card'
import {MikMakButtonBlock} from '@/products/where-to-buy/mikmak/mikmak-button'
import type {MikmakButtonContentItem} from '@/products/where-to-buy/mikmak/mikmak-button'
import {ReusableCTAButtonComponent} from '../cta-button'
import type {CtaButtonContentItem} from '../cta-button'

export type EnhancedCardContentItem = IContentItem<{
	title: Elements.TextElement
	cardTitleInCenter: Elements.MultipleChoiceElement
	image: Elements.AssetsElement
	imageOnLeft: Elements.MultipleChoiceElement
	content: Elements.RichTextElement
	cardContentInCenter: Elements.MultipleChoiceElement
	button: Elements.LinkedItemsElement<
		CtaButtonContentItem | MikmakButtonContentItem
	>
	stackButtons: Elements.MultipleChoiceElement
	hasTitleLink: Elements.MultipleChoiceElement
	titleUrl: Elements.TextElement
	makeTheImageAClickableLink: Elements.MultipleChoiceElement
	imageClickDestinationUrl: Elements.TextElement
	removeBorders: Elements.MultipleChoiceElement
	openInNewTab: Elements.MultipleChoiceElement
}>

const getClickableImageUrl = ({
	imageClickDestinationUrl,
	firstButtonUrl,
	makeTheImageAClickableLink,
	locale,
}: {
	imageClickDestinationUrl: string
	firstButtonUrl?: string
	makeTheImageAClickableLink: {codename?: string}[]
	locale: string
}): string => {
	if (makeTheImageAClickableLink[0]?.codename !== 'yes') {
		return ''
	}

	if (imageClickDestinationUrl !== '') {
		return addLocalePrefix(imageClickDestinationUrl, locale)
	}

	if (firstButtonUrl) {
		return addLocalePrefix(firstButtonUrl, locale)
	}

	return ''
}

const getMobileHref = ({
	firstButtonUrl,
	hasTitleLink,
	titleUrl,
	locale,
}: {
	firstButtonUrl: string | undefined
	hasTitleLink: {codename?: string}[]
	titleUrl: string
	locale: string
}): string | null => {
	if (hasTitleLink[0]?.codename === 'yes' && titleUrl !== '') {
		return addLocalePrefix(titleUrl, locale)
	}

	return firstButtonUrl ? addLocalePrefix(firstButtonUrl, locale) : null
}

const renderCardTitle = ({
	title,
	titleUrl,
	locale,
	openInNewTab,
	hasTitleLink,
}: {
	title: string
	titleUrl: string
	locale: string
	openInNewTab: boolean
	hasTitleLink: {codename?: string}[]
}): ReactNode => {
	if (!(hasTitleLink[0]?.codename === 'yes' && titleUrl !== '')) {
		return <span data-kontent-element-codename="card_title">{title}</span>
	}

	return (
		<Link
			href={addLocalePrefix(titleUrl, locale)}
			prefetch={false}
			rel={openInNewTab ? 'noopener noreferrer' : undefined}
			target={openInNewTab ? '_blank' : '_self'}
		>
			<span data-kontent-element-codename="card_title">{title}</span>
		</Link>
	)
}

type FooterRenderContext = Omit<
	Parameters<Block<EnhancedCardContentItem>>[0],
	'block'
>

const renderFooterButton = ({
	button,
	index,
	arr,
	removeBorders,
	stackButtons,
	...context
}: {
	button: Tersed<CtaButtonContentItem | MikmakButtonContentItem>
	index: number
	arr: Tersed<CtaButtonContentItem | MikmakButtonContentItem>[]
	removeBorders: boolean
	stackButtons: boolean
} & FooterRenderContext): ReactElement => {
	if (button.system.type === 'cta_button') {
		return (
			<ReusableCTAButtonComponent
				block={button as Tersed<CtaButtonContentItem>}
				className={clsx(
					'mb-2',
					arr.length === 1 ? 'w-full' : null,
					index < arr.length - 1 && arr.length !== 1 ? 'mr-4' : null,
					removeBorders || stackButtons ? 'w-full' : null
				)}
				key={button.system.id}
				{...context}
			/>
		)
	}

	return (
		<div
			className={clsx(
				'mb-2 inline-block',
				arr.length === 1 ? 'w-full' : null,
				index < arr.length - 1 && arr.length !== 1 ? 'mr-4' : null,
				removeBorders || stackButtons ? 'w-full' : null,
				// Override MikMakButtonBlock's own `text-center` so its
				// button aligns left within this card only.
				'[&>div]:text-left',
				// MikMakButtonBlock wraps its button in a div with
				// `my-6 md:my-8` spacing and adds `mt-2` on the button
				// itself, meant for standalone use. Zero those out here
				// so the gap matches the CTA button's spacing, which
				// relies solely on this wrapper's own `mb-2`/`mr-4`.
				'[&>div]:my-0 [&_.swn-tag-wtb-btn]:mt-0',
				// The `w-full` rules above only size this wrapper, so
				// propagate full width down to the actual button too.
				(arr.length === 1 || removeBorders || stackButtons) &&
					'[&_.swn-tag-wtb-btn]:w-full'
			)}
			data-kontent-item-id={button.system.id}
			key={button.system.id}
		>
			<MikMakButtonBlock
				block={button as Tersed<MikmakButtonContentItem>}
				{...context}
			/>
		</div>
	)
}

const renderFooterButtons = ({
	buttons,
	removeBorders,
	stackButtons,
	...context
}: {
	buttons: Tersed<CtaButtonContentItem | MikmakButtonContentItem>[]
	removeBorders: boolean
	stackButtons: boolean
} & FooterRenderContext): ReactElement => {
	return (
		<>
			{buttons.map((button, index, arr) =>
				renderFooterButton({
					button,
					index,
					arr,
					removeBorders,
					stackButtons,
					...context,
				})
			)}
		</>
	)
}

export const EnhancedCardBlock: Block<EnhancedCardContentItem> = ({
	block: {
		elements: {
			button,
			content,
			hasTitleLink,
			image,
			imageOnLeft,
			makeTheImageAClickableLink,
			removeBorders,
			stackButtons,
			title,
			cardTitleInCenter,
			titleUrl,
			cardContentInCenter,
			imageClickDestinationUrl,
			openInNewTab,
		},
		system: {codename},
	},
	...context
}) => {
	const {locale = ''} = useRouter()
	const firstButtonUrl =
		button[0]?.system.type === 'cta_button'
			? (button[0] as unknown as Tersed<CtaButtonContentItem>).elements
					.url
			: undefined
	const clickableImageUrl = getClickableImageUrl({
		imageClickDestinationUrl,
		firstButtonUrl,
		makeTheImageAClickableLink,
		locale,
	})
	const openInNewTabFlag = openInNewTab[0]?.codename === 'yes'
	const removeBordersFlag = removeBorders[0]?.codename === 'yes'
	const stackButtonsFlag = stackButtons[0]?.codename === 'yes'
	const mobileHref = getMobileHref({
		firstButtonUrl,
		hasTitleLink,
		titleUrl,
		locale,
	})
	const isCardContentInCenter = cardContentInCenter[0]?.codename === 'yes'
	return (
		<Card
			cardTitleInCenter={cardTitleInCenter[0]?.codename === 'yes'}
			className={clsx(
				removeBordersFlag ? undefined : 'p-4',
				'h-full flex-none text-black'
			)}
			data-kontent-element-codename="enhanced_card"
			imageOnLeft={imageOnLeft[0]?.codename === 'yes'}
			mobileOnlyHref={mobileHref}
			mobileOnlyHrefLabel={title}
			openInNewTab={openInNewTabFlag}
			removeBorders={removeBordersFlag}
			title={renderCardTitle({
				title,
				titleUrl,
				locale,
				openInNewTab: openInNewTabFlag,
				hasTitleLink,
			})}
		>
			{image[0] ? (
				<CardImage
					alt={image[0].description}
					className={clsx(
						'h-48',
						removeBordersFlag ? 'object-cover' : '',
						codename.startsWith('brand')
							? 'object-cover'
							: 'object-contain'
					)}
					data-kontent-element-codename="image"
					imageUrl={clickableImageUrl}
					openInNewTab={openInNewTabFlag}
					src={image[0].url}
				/>
			) : null}
			<div
				className={`content m-0 w-full break-words ${
					isCardContentInCenter ? 'text-center' : ''
				}`}
				dangerouslySetInnerHTML={{__html: content.value}}
				data-kontent-element-codename="text"
			/>
			<CardFooter>
				{renderFooterButtons({
					buttons: button,
					removeBorders: removeBordersFlag,
					stackButtons: stackButtonsFlag,
					...context,
				})}
			</CardFooter>
		</Card>
	)
}
